import {
  Box,
  Checkbox,
  Chip,
  FormControlLabel,
  Tooltip,
  Typography,
} from "@mui/material";
import type { ExtractionImage } from "../../../types/extraction";
import { EXTRACTION_IMAGE_TYPE_LABELS } from "../../../types/extraction";
import { CustomButton } from "../../../components/common/customButton";

interface ExtractionImageCardProps {
  image: ExtractionImage;
  variant?: "compact" | "large";
  onImageError?: (image: ExtractionImage) => void;
  selectable?: boolean;
  selectionUpdating?: boolean;
  onToggleSelected?: (image: ExtractionImage) => void;
  onSetPrimary?: (image: ExtractionImage) => void;
}

export function ExtractionImageCard({
  image,
  variant = "compact",
  onImageError,
  selectable = false,
  selectionUpdating = false,
  onToggleSelected,
  onSetPrimary,
}: ExtractionImageCardProps) {
  const typeLabel =
    EXTRACTION_IMAGE_TYPE_LABELS[image.image_type] ?? image.image_type;

  return (
    <Box
      sx={{
        border: image.is_selected ? "2px solid #0E4664" : "1px solid #e0e0e0",
        borderRadius: 1,
        overflow: "hidden",
        backgroundColor: "background.paper",
      }}
    >
      <Box
        role={selectable ? "button" : undefined}
        tabIndex={selectable ? 0 : undefined}
        aria-pressed={selectable ? image.is_selected : undefined}
        aria-label={
          selectable
            ? `${image.is_selected ? "Deselect" : "Select"} image for deal`
            : undefined
        }
        onClick={() => {
          if (selectable && !selectionUpdating) {
            onToggleSelected?.(image);
          }
        }}
        onKeyDown={(e) => {
          if (
            selectable &&
            !selectionUpdating &&
            (e.key === "Enter" || e.key === " ")
          ) {
            e.preventDefault();
            onToggleSelected?.(image);
          }
        }}
        sx={{
          display: "block",
          position: "relative",
          aspectRatio: variant === "large" ? "16/10" : "4/3",
          backgroundColor: "#f5f5f5",
          cursor:
            selectable && !selectionUpdating ? "pointer" : "default",
        }}
      >
        <Box
          component="img"
          src={image.url}
          alt={image.caption ?? typeLabel}
          onError={() => onImageError?.(image)}
          sx={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            display: "block",
          }}
        />
        {image.is_hero && (
          <Chip
            label="Primary"
            size="small"
            sx={{
              position: "absolute",
              top: 8,
              left: 8,
              backgroundColor: "#0E4664",
              color: "#fff",
              fontWeight: 600,
            }}
          />
        )}
        {image.is_selected && !image.is_hero && (
          <Chip
            label="Selected"
            size="small"
            sx={{
              position: "absolute",
              top: 8,
              left: 8,
              backgroundColor: "#176647",
              color: "#fff",
              fontWeight: 600,
            }}
          />
        )}
      </Box>
      <Box sx={{ p: 1.5 }}>
        {selectable && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1.5,
              mb: 2.5,
              pb: 1,
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  size="medium"
                  checked={image.is_selected}
                  disabled={selectionUpdating}
                  onClick={(e) => e.stopPropagation()}
                  onChange={() => onToggleSelected?.(image)}
                />
              }
              label={
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  Include on deal
                </Typography>
              }
              sx={{ m: 0, flex: 1, minWidth: 0 }}
            />
            {!image.is_hero && (
              <Box sx={{ flexShrink: 0, ml: "auto" }}>
                <CustomButton
                  text="Set as primary"
                  handleButtonPress={() => onSetPrimary?.(image)}
                  solid={false}
                  isButtonDisabled={selectionUpdating}
                  heightOverride="32px"
                  widthOverride="120px"
                />
              </Box>
            )}
          </Box>
        )}
        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mb: 0.5 }}>
          <Chip label={typeLabel} size="small" />
          <Chip
            label={`p.${image.page_number}`}
            size="small"
            variant="outlined"
          />
          {image.confidence != null && (
            <Chip
              label={`${Math.round(image.confidence * 100)}%`}
              size="small"
              variant="outlined"
            />
          )}
        </Box>
        {image.caption && (
          <Tooltip title={image.caption}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: variant === "large" ? 3 : 2,
                WebkitBoxOrient: "vertical",
              }}
            >
              {image.caption}
            </Typography>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
}
